import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import ProgressBar from "../helpers/progress_bar"

function fetchJson(url, options) {
  return fetch(url, options).then((response) => response.json().then((data) => ({ ok: response.ok, data })));
}

// Mounted on every root where its markup can render: the team-members-datatable wrapper on the
// two member-listing pages, and `#team-modal` on the team-editor pages and the dashboard layout's
// global modal mount. Delegating from `this.element` still reaches ajax-inserted rows, and every
// element it touches only ever renders inside one of these mounts.
export default class extends Controller {
  connect() {
    this.onRoleChange = this.onRoleChange.bind(this);
    this.onOpenDeleteModal = this.onOpenDeleteModal.bind(this);

    // `.change-role` (user_decorator.rb) and `.delete-collaborator-modal` (user_decorator.rb AND
    // the admin team-editor modal's own _user.html.haml) only ever exist inside a row rendered for
    // a DataTables ajax draw or a team-editor tab load - never at connect() - so these stay
    // delegated jQuery listeners rather than data-action (Pattern 1). The confirm button itself
    // (#delete-collaborator-button, in the shared delete_modal partial) and .close-dropdown /
    // .delete-collaborator-button in the subdomain team-editor modal's own _user.html.haml are
    // wired via data-action directly in the markup instead, since this lane owns that partial.
    $(this.element).on("change.teamMemberRoles", "select.change-role:not(.no-save)", this.onRoleChange);
    $(this.element).on("click.teamMemberRoles", ".delete-collaborator-modal", this.onOpenDeleteModal);
  }

  disconnect() {
    $(this.element).off(".teamMemberRoles");
  }

  displayChangeRoleRequestStatus(element, status, shouldFade) {
    const statusEl = element.parentElement.parentElement.parentElement.querySelector(`.remove-user-${status}`);

    if (!statusEl) {
      window.ltAlert({ message: "Changes saved!", type: "toast-success" });
      return;
    }

    statusEl.style.visibility = "visible";
    $(statusEl).fadeTo(500, 1);

    if (shouldFade) {
      setTimeout(() => {
        $(statusEl).fadeTo(500, 0, () => {
          statusEl.style.visibility = "hidden";
          statusEl.style.display = "inline-block";
        });
      }, 3000);
    }
  }

  hideAllChangeRoleRequestStatus(element) {
    element.parentElement.querySelectorAll(".remove-user-status").forEach((el) => { el.style.visibility = "hidden"; });
  }

  displayTeamMembersError(message, element) {
    const errorEl = element.parentElement.querySelector(".team-members-error");
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = "";
    }
  }

  hideTeamMembersError(element) {
    const errorEl = element.parentElement.querySelector(".team-members-error");
    if (errorEl) { errorEl.style.display = "none"; }
  }

  onRoleChange(event) {
    const select = event.currentTarget;
    const selected = select.value;

    if (selected === "") { return; }
    if (selected !== "user" && selected !== "admin") { return; }

    const url = select.dataset.url;
    const options = RequestHelper.defaultPutOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });
    options.body = JSON.stringify({ user: { admin: selected === "admin" } });

    // Subdomain::UsersController declares `respond_to :html`, so its success path always 302s to
    // an HTML page whatever headers we send. On success only the admin controller ever answers
    // with JSON, and only to carry a recurring invoicing warning.
    fetch(url, options).then((response) => {
      if (response.ok) {
        this.hideTeamMembersError(select);
        this.hideAllChangeRoleRequestStatus(select);
        this.displayChangeRoleRequestStatus(select, "success", true);

        if ((response.headers.get("Content-Type") || "").includes("application/json")) {
          response.json().then((data) => this.displayWarning(data.warning));
        }
        return;
      }

      response.json().then((data) => {
        this.displayTeamMembersError(data.error, select);
        window.ltAlert({ message: data.error, type: "toast-error" });
      });
    });
  }

  // Only admin facing endpoints send a warning. It stays up until dismissed.
  displayWarning(warning) {
    if (!warning) { return; }

    window.ltAlert({ message: warning, type: "toast-warning", duration: -1, showMultiple: true });
  }

  onOpenDeleteModal(event) {
    event.preventDefault();

    const link = event.currentTarget;
    const modal = document.getElementById("collaborator-delete-modal");
    if (!modal) { return; }

    const emailEl = modal.querySelector("#collaborator-email");
    if (emailEl) { emailEl.textContent = link.dataset.email; }
    const teamEl = modal.querySelector("#team-name");
    if (teamEl) { teamEl.textContent = link.dataset.team; }

    const deleteButton = modal.querySelector("#delete-collaborator-button");
    if (deleteButton) {
      deleteButton.setAttribute("href", link.dataset.url);
      deleteButton.dataset.slug = link.dataset.slug;
      deleteButton.dataset.tabreload = link.dataset.tabreload;
    }
    $(modal).modal("show");
  }

  onDeleteCollaborator(event) {
    event.preventDefault();

    const link = event.currentTarget;
    const modal = document.getElementById("collaborator-delete-modal");
    link.classList.add("disabled");

    const options = RequestHelper.defaultDeleteOptions();
    options.headers = Object.assign({}, options.headers, {
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });

    fetchJson(`${link.getAttribute("href")}?slug=${link.dataset.slug}`, options).then(({ ok, data }) => {
      if (!ok) {
        window.ltAlert({ message: data.message, type: "toast-error" });
        link.classList.remove("disabled");
        return;
      }

      window.ltAlert({ message: data.message });
      this.displayWarning(data.warning);

      // team-editor and team-member-roles are always mounted on the SAME element, so its
      // instance is looked up on `this.element` itself, not on `#team-modal`, which is a
      // descendant it reaches rather than where its data-controller lives.
      const tabUrl = document.querySelector("#team-modal .nav li.active a")?.getAttribute("data-url");
      if (tabUrl !== undefined && tabUrl !== null) {
        const editor = this.application.getControllerForElementAndIdentifier(this.element, "team-editor");
        if (editor) { editor.loadTabData(tabUrl); }
      }

      if (modal) { $(modal).modal("hide"); }

      // Deliberately unscoped: the table this refreshes is often the teams-datatable, owned by
      // team_table_controller.js rather than by this mount.
      this.reload();
      link.classList.remove("disabled");
    });
  }

  onCloseDropdown(event) {
    event.preventDefault();
    $(event.currentTarget).closest(".dropdown-menu").prev().dropdown("toggle");
  }

  // Page-global `$(".datatable")` - see above. No page this mounts on renders a
  // `.datatable-filter` element, so there is no filter-scraping branch here.
  reload(extraParams = {}) {
    ProgressBar.start();

    const datatable = document.querySelector(".datatable");
    if (!datatable) { ProgressBar.stop(); return; }

    const params = Object.assign(this.currentParams(datatable), extraParams);
    const replacement = `${datatable.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    datatable.setAttribute("data-source", replacement);
    $(datatable).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams(datatable) {
    return this.urlParamsAsObject(datatable.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string into the nested object `$.param()` re-serializes
  // back into the same form. A URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }
}
